import React, { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  startAfter,
  limit,
  orderBy,
  doc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../FirebaseConfig";
import ProfilePicture from "../UserProfile/ProfilePicture";
import { onSnapshot } from "firebase/firestore";
import { Link } from "react-router-dom";
import { formatTimestampForPost } from "../lib/formatTimestampForPost";
import useNotification from "../hooks/useNotification";
import usePointsStore from "../StatesStore/PointsTable";
import usePoints from "../hooks/user/usePoints";
import useFeedNotification from "../hooks/useFeedNotification";
import AddComment from "./AddComment";
import { Accordion } from "react-bootstrap";

function Comment({ comment, user, post, handleDeleteComment }) {
  // console.log("newComments comment", comment);
  const [replyCount, setReplyCount] = useState(comment?.replies?.length || 0);
  return (
    <li className="comment-item mb-2 mt-0">
      <div className="d-flex">
        {/* Avatar */}
        <div className="avatar avatar-sm ">
          <ProfilePicture userData={comment} className="avatar-img rounded-2" />
        </div>
        {/* Comment by */}
        <div className="ms-2 w-100">
          <div className="bg-light rounded-start-top-0 p-2 rounded">
            <div className="d-flex justify-content-between mb-1 w-100">
              <h6 className="mb-0">
                {" "}
                <Link
                  to={
                    comment ? `/userProfile/${comment.userId}` : "/userProfile"
                  }
                >
                  {" "}
                  {comment?.userName
                    .split(" ")
                    .slice(0, 3)
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}{" "}
                </Link>{" "}
              </h6>
              <span className="mb-0">
                {formatTimestampForPost(comment?.timestamp)}
              </span>
            </div>
            <p className="mb-0 h6 fw-light text-body">{comment?.content}</p>
          </div>

          <>
            <div className="mx-1 pe-2 w-100 d-flex justify-content-between commentSection">
              <Accordion flush className="w-100">
                <style jsx>
                  {`
                    .commentSection .accordion-button::after {
                      display: none !important; /* Hides the default arrow */
                    }
                  `}
                </style>
                <Accordion.Item className="bg-transparent" eventKey={0}>
                  <Accordion.Header className="d-inline-block">
                    <Link
                      to=""
                      className="fw-light h6 m-0 p-0 ps-2"
                      style={{ fontSize: "0.7rem" }}
                    >
                      Reply {replyCount > 0 && `(${replyCount})`}
                    </Link>
                  </Accordion.Header>
                  <Accordion.Body className="p-0 m-0 mt-1">
                    {comment?.replies.length !== 0 &&
                      comment?.replies?.map((com, index) => {
                        return (
                          <Comment
                            comment={com}
                            key={index}
                            post={post}
                            user={user}
                            handleDeleteComment={handleDeleteComment}
                          />
                        );
                      })}
                    <div className="ms-2">
                      <AddComment
                        post={comment}
                        user={user}
                        setCommentCount={setReplyCount}
                        type={"reply"}
                      />
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
              {(post?.userId === user?.uid ||
                comment?.userId === user?.uid) && (
                <Link
                  to=""
                  className=" fw-light h6 m-0 pt-1 position-absolute"
                  style={{ right: "1%", fontSize: "0.7rem" }}
                  onClick={() =>
                    handleDeleteComment(comment?.reference, comment.id)
                  }
                >
                  Delete
                </Link>
              )}
            </div>
          </>
        </div>
      </div>
    </li>
  );
}

export default function ShowComment({
  post,
  commentCount,
  setCommentCount,
  user,
  visibleComments,
  setVisibleComments,
}) {
  const [comments, setComments] = useState([]);
  // const [visibleComments, setVisibleComments] = useState(3);
  const [loading, setLoading] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const commentRef = useRef();
  const { deleteNotification } = useNotification();
  const pointsTable = usePointsStore((state) => state.pointsTable);
  const { removePoints } = usePoints();
  //const [countLeft, setCountLeft] = useState(0);
  const { deleteFeedNotification } = useFeedNotification();

  const fetchComments = async () => {
    const commentsCollectionRef = collection(db, "posts", post?.id, "comments");
    let q;
    if (lastVisible) {
      q = query(
        commentsCollectionRef,
        orderBy("timestamp", "desc"),
        startAfter(lastVisible),
        limit(3)
      );
    } else {
      q = query(commentsCollectionRef, orderBy("timestamp", "desc"), limit(3));
    }

    // Fetch replies recursively with onSnapshot
    const fetchReplies = (collectionRef, docID) => {
      const replyRef = collection(collectionRef, docID, "replies");
      const q = query(replyRef, orderBy("timestamp", "desc"));

      // Use onSnapshot for replies
      const unsubscribeReplies = onSnapshot(q, async (replySnapshot) => {
        const replies = await Promise.all(
          replySnapshot.docs.map(async (reply) => {
            let returnObj = {
              id: reply?.id,
              ...reply?.data(),
              userName: "",
              photo: "",
              reference: replyRef,
              replies: [], // Default to an empty array for nested replies
            };

            const userDocRef = doc(db, "users", reply?.data().userId);
            const userSnapshot = await getDoc(userDocRef);

            if (userSnapshot.exists()) {
              returnObj.userName = userSnapshot.data().userName;
              returnObj.photo = userSnapshot.data().photo;
            } else {
              console.error(
                "User document not found for userId:",
                reply?.data().userId
              );
            }

            // Recursively fetch nested replies
            returnObj.replies = await fetchReplies(replyRef, reply?.id);

            return returnObj;
          })
        );

        // Update the replies within the comment
        setComments((prevComments) =>
          prevComments.map((comment) => {
            if (comment.id === docID) {
              return { ...comment, replies }; // Update replies for the correct comment
            }
            return comment;
          })
        );
      });

      return unsubscribeReplies;
    };

    // Use onSnapshot for comments
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const newComments = await Promise.all(
        querySnapshot.docs.map(async (doc) => {
          return {
            id: doc.id,
            ...doc.data(),
            userName: "",
            photo: "",
            reference: commentsCollectionRef,
            replies: [], // Initialize replies to an empty array
          };
        })
      );

      // Fetch user details and replies for each comment
      Promise.all(
        newComments.map(async (comment) => {
          const userDocRef = doc(db, "users", comment.userId);
          const userSnapshot = await getDoc(userDocRef);

          if (userSnapshot.exists()) {
            comment.userName = userSnapshot.data().userName;
            comment.photo = userSnapshot.data().photo;
          } else {
            console.error(
              "User document not found for userId:",
              comment.userId
            );
          }

          // Fetch replies for this comment using onSnapshot
          fetchReplies(commentsCollectionRef, comment.id);
        })
      )
        .then(() => {
          setComments((prevComments) => {
            if (lastVisible) {
              return [...newComments, ...prevComments];
            } else {
              return newComments;
            }
          });
        })
        .catch((error) => {
          console.error("Error fetching user data:", error);
        });

      if (newComments.length > 0) {
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  };
  useEffect(() => {
    if (commentRef && commentRef.current) {
      commentRef.current.scrollTop = commentRef.current.scrollHeight;
    }
  }, [comments]);
  useEffect(() => {
    fetchComments();
  }, [post.id]);

  const loadMoreComments = () => {
    setLoading(true);
    fetchComments();
    setVisibleComments(commentCount);
    // setVisibleComments(commentCount);
    setLoading(false);
  };
  const handleDeleteComment = async (reference, commentUID) => {
    // console.log(commentUID);
    const commentDoc = doc(reference, commentUID);
    await deleteDoc(commentDoc);
    setCommentCount((prev) => prev - 1);
    if (post.userId !== user?.uid) {
      await deleteNotification({
        userId: post?.userId,
        id: user?.uid,
        type: "postComment",
        by: "user",
        fields: {
          postId: post?.id,
        },
      });
    }

    //deleting points
    const postingPoints = pointsTable.commentingOnUserPost || 0;
    removePoints({
      userId: user?.uid,
      type: "commentingOnUserPost",
      pointsRemoved: postingPoints,
      fields: {
        postId: post?.id,
      },
    });

    deleteFeedNotification({
      ownerId: post.userId,
      id: user?.uid,
      type: "postComment",
      by: "user",
      fields: {
        postId: post?.id,
      },
    });
  };
  useEffect(() => {
    console.log("visibleComments", visibleComments);
    console.log("visibleComments commentCount", commentCount);
    // if (visibleComments === 3) setLastVisible(false);
    // console.log("visible  comments", comments);
  }, [visibleComments]);

  return (
    <>
      {commentCount > visibleComments && !loading && (
        <div className="card-footer border-0 py-1 px-0">
          <button
            className="btn btn-link btn-link-loader btn-sm text-secondary d-flex align-items-center"
            onClick={loadMoreComments}
            disabled={loading}
          >
            <div className="spinner-dots me-2">
              <span className="spinner-dot"></span>
              <span className="spinner-dot"></span>
              <span className="spinner-dot"></span>
            </div>
            View older comments
          </button>
        </div>
      )}
      {/* Comment wrap START */}
      <ul
        className="comment-item-nested list-unstyled mb-0 px-0"
        ref={commentRef}
        style={{
          maxHeight: "18rem",
          overflowY: commentCount > visibleComments ? "hidden" : "auto",
          overflowX: "hidden",
          // scrollbarWidth: `${
          //   commentCount >= visibleComments ? "none" : "auto"
          // }`,
        }}
      >
        {/* Comment item START */}
        {comments?.map((comment, index) => {
          return (
            <Comment
              comment={comment}
              key={index}
              post={post}
              user={user}
              handleDeleteComment={handleDeleteComment}
            />
          );
        })}

        {/* Comment item END */}
      </ul>
      {/* Comment wrap END */}
    </>
  );
}
